import React, { useState, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LayersIcon from '@mui/icons-material/Layers';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import type { NewsArticle } from '../../types/News';
import { sendNewsEvent } from '../../services/newsApi';

interface NewsCardProps {
  article: NewsArticle;
  bookmarked: boolean;
  /** クッションを開いて中身を確認済みか */
  seen: boolean;
  onToggleBookmark: (article: NewsArticle) => void;
  /** クラスタの中身を開く。related_count > 0 のときだけ使う */
  onOpenRelated: (articleId: number) => void;
  /** カード本体を押したときに開くクッション */
  onOpenDetail: (articleId: number) => void;
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 60) return `${Math.max(diffMin, 1)}分前`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}時間前`;
  const diffDay = Math.floor(diffMin / (60 * 24));
  if (diffDay < 7) return `${diffDay}日前`;
  return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' });
};

/**
 * ニュース1件のカード。
 *
 * **どの記事も同じ寸法で出す。** 以前は先頭だけ大きなヒーローで、
 * 以降は 116px のサムネイルという作りだったが、画像の扱いが揃わず
 * 一覧としての見通しが悪かった。グリッドに同じ形で並べたほうが速く読める。
 */
const NewsCard: React.FC<NewsCardProps> = ({
  article, bookmarked, seen, onToggleBookmark, onOpenRelated, onOpenDetail,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const navigate = useNavigate();

  // クラスタは最も確信度の高い紐づけゲームで束ねている（サーバー側と同じ基準）
  const clusterGame = article.games[0];
  const clusterName = clusterGame
    ? clusterGame.japanese_name || clusterGame.name
    : null;

  // 画像の失敗はフェーズ1(ホットリンク検証)の判断材料になるのでビーコンを送る。
  // 1記事につき1回だけ（再レンダリングで多重送信しないよう state で止める）
  const handleImageError = useCallback(() => {
    if (!imageFailed) {
      setImageFailed(true);
      sendNewsEvent(article.id, 'image_error');
    }
  }, [article.id, imageFailed]);

  // 外部サイトへ直行させず、まずクッションを開く。
  // クリック計測はクッションの「記事を読む」側で送る（実際に読んだ数を測るため）
  const handleOpen = useCallback(() => {
    onOpenDetail(article.id);
  }, [article.id, onOpenDetail]);

  const showImage = article.image && !imageFailed;

  return (
    <Box
      onClick={handleOpen}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'rgba(255,255,255,0.035)',
        // 画面外のカードはブラウザに描画を省かせる。
        // 無限スクロールでカードが数百枚積み上がったとき、
        // スタイル計算・レイアウト・ペイントの対象から外れるのが効く。
        // contain-intrinsic-size の auto は「最後に描画したときの高さ」を
        // 覚えさせる指定で、これが無いとスクロールバーが跳ねる。
        // 非対応ブラウザでは単に無視される（見た目は変わらない）
        contentVisibility: 'auto',
        containIntrinsicSize: 'auto 260px',

        border: '1px solid rgba(255,255,255,0.07)',
        // カード自体は動かさない（一覧をなぞるたびに跳ねると読みにくい）。
        // 動くのは画像だけに留める
        transition: 'border-color .18s ease, background-color .18s ease',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.16)',
          bgcolor: 'rgba(255,255,255,0.055)',
        },
        '&:hover .news-thumb': { transform: 'scale(1.04)' },
      }}
    >
      {/* 画像。寸法が事前に分からないので比率を固定する（CLS抑制） */}
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden' }}>
        {showImage ? (
          <Box
            component="img"
            className="news-thumb"
            src={article.image!.url}
            alt=""
            loading="lazy"
            // 一部媒体はリファラでホットリンクを弾くため送らない
            referrerPolicy="no-referrer"
            onError={handleImageError}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              transition: 'transform .4s ease, opacity .2s ease',
              // 既読は画像を落として見出しを弱める。
              // カード全体を薄くすると一覧が濁って見えるので、
              // 目線が最初に行く2箇所だけに留める
              opacity: seen ? 0.45 : 1,
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.04)',
              color: 'rgba(255,255,255,0.18)',
            }}
          >
            <NewspaperIcon sx={{ fontSize: 34 }} />
          </Box>
        )}

        {/* 下端のグラデーション。媒体名を画像の明暗に関わらず読ませるため */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.15) 34%, rgba(0,0,0,0) 58%)',
            pointerEvents: 'none',
          }}
        />

        <Typography
          sx={{
            position: 'absolute',
            left: 9,
            bottom: 7,
            // バッジと同じ行なので、右端に余白を残して重ならないようにする
            maxWidth: article.related_count > 0 ? 'calc(100% - 62px)' : 'calc(100% - 18px)',
            fontSize: { xs: '0.5rem', sm: '0.55rem' },
            fontWeight: 500,
            letterSpacing: '.02em',
            // 既読は画像を落とすので、その上に乗る媒体名も合わせて弱める
            color: seen ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.8)',
            textShadow: '0 1px 3px rgba(0,0,0,0.7)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {article.source.name}
        </Typography>

        {/* 畳んだクラスタの入口。アイコンと件数だけに留める。
            媒体名と同じ行に置くので、幅を食うと媒体名を潰してしまう */}
        {article.related_count > 0 && (
          <Tooltip title={clusterName ? `『${clusterName}』の記事をまとめて見る` : '同じ話題の記事を見る'}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onOpenRelated(article.id);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 7,
                display: 'flex',
                alignItems: 'center',
                gap: 0.3,
                px: 0.7,
                py: 0.2,
                borderRadius: 10,
                cursor: 'pointer',
                bgcolor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                border: '1px solid rgba(217,255,0,0.35)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
              }}
            >
              <LayersIcon sx={{ fontSize: 11, color: 'primary.main' }} />
              <Typography sx={{ fontSize: { xs: '0.5rem', sm: '0.55rem' }, fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}>
                {article.related_count + 1}
              </Typography>
            </Box>
          </Tooltip>
        )}

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleBookmark(article);
          }}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 28,
            height: 28,
            bgcolor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: bookmarked ? 'primary.main' : 'rgba(255,255,255,0.75)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
          }}
        >
          {bookmarked ? (
            <BookmarkIcon sx={{ fontSize: 16 }} />
          ) : (
            <BookmarkBorderIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>

      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.7rem' },
            fontWeight: seen ? 500 : 600,
            lineHeight: 1.45,
            color: seen ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.92)',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {article.title}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {(() => {
          // 関連バッジの有無に関わらずゲーム名は出す。
          // バッジ側は件数だけでゲーム名を持たないので、隠す理由がない
          const chips = article.games;
          return chips.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {chips.slice(0, 2).map((g) => (
              <Box
                key={g.game_id}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/game/${g.game_id}`);
                }}
                sx={{
                  maxWidth: '100%',
                  px: 0.85,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: { xs: '0.5rem', sm: '0.55rem' },
                  // 既読はカード全体の彩度を落とす。チップだけ明るいと浮く
                  color: seen ? 'rgba(217,255,0,0.5)' : 'primary.main',
                  bgcolor: seen ? 'rgba(217,255,0,0.05)' : 'rgba(217,255,0,0.09)',
                  border: seen
                    ? '1px solid rgba(217,255,0,0.1)'
                    : '1px solid rgba(217,255,0,0.18)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': { bgcolor: 'rgba(217,255,0,0.16)' },
                }}
              >
                {g.japanese_name || g.name}
              </Box>
            ))}
          </Box>
          );
        })()}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 1 }}>
          {/* 既読の印。色の変化だけだと「薄いだけ」に見えるので記号も添える */}
          {seen && <CheckIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }} />}
          <Typography sx={{
            fontSize: { xs: '0.5rem', sm: '0.55rem' },
            color: 'rgba(255,255,255,0.4)',
          }}>
            {seen ? '既読' : formatDate(article.published_at)}
          </Typography>
          {seen && (
            <Typography sx={{
              fontSize: { xs: '0.5rem', sm: '0.55rem' },
              color: 'rgba(255,255,255,0.25)',
            }}>
              · {formatDate(article.published_at)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NewsCard;
